'use client';

import React from 'react';
import Link from 'next/link';
import { cn } from '../../lib/utils';
import { useFocusEffect } from '../../hooks';
import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import { type Project } from '@/app/lib/data';
import { useTranslation } from 'react-i18next';

/**
 * RelatedProjects component
 * Displays a list of projects related to the current project
 * Enhanced with animations and styling consistent with the rest of the portfolio
 */
interface RelatedProjectsProps {
  projects: Project[];
  className?: string;
}

export function RelatedProjects({ projects, className }: RelatedProjectsProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(containerRef.current.children,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: "power2.out" }
      );
    }
  }, []);

  if (!projects || projects.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('space-y-6 relative', className)}
    >
      {/* Decorative element */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-slate-300/20 to-slate-500/10 rounded-full blur-3xl dark:from-slate-800/30 dark:to-slate-900/20 -z-10" />

      <div className="flex items-center gap-2 pb-2">
        <Icon
          icon="carbon:connection"
          className="h-5 w-5 text-slate-900 dark:text-slate-200"
        />
        <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">{t('Projects.recentTitle')}</h2>
        <div className="h-px flex-grow bg-gradient-to-r from-slate-300 to-transparent dark:from-slate-700 ml-2" />
      </div>

      <div
        ref={containerRef}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {projects.map((project) => (
          <div key={project._id}>
            <RelatedProjectCard project={project} />
          </div>
        ))}
      </div>
    </div>
  );
}

function RelatedProjectCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { isFocused, ref, focusProps } = useFocusEffect<HTMLDivElement>();

  return (
    <Link href={`/projects/${project.slug?.current || ''}`} className="block group h-full">
      <div
        ref={ref}
        className={cn(
          'flex flex-col h-full p-5 rounded-xl border',
          'bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800/80',
          'border-slate-200 dark:border-slate-700/50 transition-all duration-300',
          'hover:border-slate-300 dark:hover:border-slate-600 hover:-translate-y-1.5',
          isFocused ? 'shadow-lg ring-1 ring-slate-300/50 dark:ring-slate-600/30 translate-y-[-6px]' : 'shadow-sm',
        )}
        {...focusProps}
      >
        <h3 className={cn(
          "text-lg font-semibold mb-2 transition-colors duration-300",
          "text-slate-800 dark:text-slate-200",
          isFocused ? "text-slate-900 dark:text-white" : ""
        )}>
          {project.translationKey ? t(`Projects.items.${project.translationKey}.title`) : project.title}
        </h3>

        <p className={cn(
          "text-slate-600 dark:text-slate-400 text-sm line-clamp-2 flex-grow mb-4 transition-colors duration-300",
          isFocused ? "text-slate-700 dark:text-slate-300" : ""
        )}>
          {project.translationKey ? t(`Projects.items.${project.translationKey}.description`) : project.description}
        </p>

        {/* Technologies */}
        {project.technologies && project.technologies.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {project.technologies.slice(0, 3).map((tech) => (
              <span
                key={typeof tech === 'string' ? tech : tech._id}
                className={cn(
                  "px-2 py-1 text-xs rounded-md transition-colors duration-300",
                  isFocused
                    ? "bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    : "bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400"
                )}
              >
                {typeof tech === 'string' ? tech : tech.name}
              </span>
            ))}
            {project.technologies.length > 3 && (
              <span className="px-2 py-1 text-xs text-slate-500 dark:text-slate-500">
                +{project.technologies.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Link-like Span */}
        <div className="mt-auto transition-transform group-hover:translate-x-1 duration-200">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-sm transition-all duration-300",
              "text-slate-700 dark:text-slate-300",
              isFocused ? "text-slate-900 dark:text-white translate-x-1" : "group-hover:text-slate-900 dark:group-hover:text-white group-hover:translate-x-1"
            )}
          >
            {t('Projects.viewProject')} <Icon
              icon="carbon:arrow-right"
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-300",
                isFocused ? "translate-x-1" : "group-hover:translate-x-1"
              )}
            />
          </span>
        </div>
      </div>
    </Link>
  );
} 